// Mutations de escrita do detalhe do envio — reenvio/reprocesso —
// com UPDATE OTIMISTA + rollback + toast (mesmo padrão das
// provider-mutations/configuration-mutations).
//
// Onda 2 (front-first): o gateway responde um resultado ESTÁTICO
// (mocks/notification-center/resend-result.json), então NÃO
// invalidamos a query no sucesso — refetch traria o mock original e
// desfaria a escrita. Na onda 4 (writes reais) troca-se por
// invalidateQueries no onSettled.
import { useTranslation } from "react-i18next"

import { useToast } from "@/hooks/use-toast"
import { useMutation, useQueryClient } from "@/libs"
import { postNotificationCenterNotificationResend } from "@/services/notification-center"
import type {
  NotificationDetailType,
  NotificationResendResultType,
} from "@/types/notification-center"

import { getNotificationDetailQueryKey } from "./hooks"

const detailPrefix =
  "pages.notificationCenter.notificationDetailPage"

// Status intermediário do reenvio (contrato do resend-result.json).
export const RESEND_STATUS = "WAITING_TO_RESEND"

// Contexto de rollback da mutation de reenvio.
type DetailSnapshotContext = {
  previous?: NotificationDetailType
}

// Aplica o reenvio otimista a UM canal do envio: o canal alvo volta
// para a fila (macro QUEUED), perde o erro e ganha um evento
// WAITING_TO_RESEND no topo da sua timeline (seq = max + 1). Os demais
// canais do envio ficam intactos.
export const applyResendToChannel = (
  detail: NotificationDetailType,
  channelId: string,
  requestedAt: string
): NotificationDetailType => ({
  ...detail,
  channels: detail.channels.map((channel) => {
    if (channel.id !== channelId) {
      return channel
    }
    const nextSeq =
      channel.timeline.reduce(
        (max, entry) => Math.max(max, entry.seq),
        0
      ) + 1
    return {
      ...channel,
      status: RESEND_STATUS,
      macroStatus: "QUEUED",
      error: null,
      timeline: [
        ...channel.timeline,
        {
          seq: nextSeq,
          status: RESEND_STATUS,
          macroStatus: "QUEUED",
          datetime: requestedAt,
          detail: null,
        },
      ],
    }
  }),
})

// POST …/notifications/:channelId/resend — reenvia UM canal do envio
// (o target/NTGT). Otimista: o badge do canal muda para "Em fila" e a
// timeline dele ganha o evento de reenvio antes do ack do servidor. A
// query key é a do ENVIO (schedulerId), pois o cache guarda o payload
// único do scheduler com todos os canais.
export const useResendNotification = (
  schedulerId: string,
  channelId: string
) => {
  const { t } = useTranslation()
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const queryKey =
    getNotificationDetailQueryKey(schedulerId)

  return useMutation<
    NotificationResendResultType,
    Error,
    void,
    DetailSnapshotContext
  >({
    mutationFn: () =>
      postNotificationCenterNotificationResend(channelId),
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey })
      const previous =
        queryClient.getQueryData<NotificationDetailType>(
          queryKey
        )
      if (previous) {
        queryClient.setQueryData(
          queryKey,
          applyResendToChannel(
            previous,
            channelId,
            new Date().toISOString()
          )
        )
      }
      return { previous }
    },
    onError: (_error, _variables, context) => {
      if (context?.previous) {
        queryClient.setQueryData(queryKey, context.previous)
      }
      toast({
        title: t(`${detailPrefix}.resend.error`),
        variant: "destructive",
      })
    },
    onSuccess: () => {
      toast({
        title: t(`${detailPrefix}.resend.success`),
      })
    },
  })
}
